import React, { useState, useEffect, useCallback } from 'react';
import { clientsApi, integrationsApi } from '../utils/api'; // Importer aussi integrationsApi
import debounce from 'lodash.debounce'; // Importer debounce
import ClientDetails from '../components/clients/ClientDetails'; // Importer le composant ClientDetails

/**
 * Composant pour la page de gestion des clients.
 * Affiche la liste des clients et permet l'importation depuis Shopify.
 */
const Clients = () => {
  // États pour stocker les clients, l'état de chargement et les erreurs
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États pour l'importation Shopify
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccessMessage, setImportSuccessMessage] = useState(null);

  // Nouvel état pour gérer le chargement du calcul des points par client
  // Clé: customerId, Valeur: boolean (true si calcul en cours)
  const [calculatingPoints, setCalculatingPoints] = useState({});

  // --- NOUVEAUX ÉTATS pour Calcul Batch ---
  const [isBatchCalculating, setIsBatchCalculating] = useState(false);
  const [batchMessage, setBatchMessage] = useState(null);

  // --- NOUVEAUX ÉTATS pour Calcul par Page ---
  const [isPageCalculating, setIsPageCalculating] = useState(false); // État de chargement pour le calcul de la page
  const [pageCalcMessage, setPageCalcMessage] = useState(null); // Message pour le calcul de la page
  
  // --- ÉTATS pour le modal de détails client ---
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  
  // ---------------------------------------

  // --- NOUVEAUX ÉTATS pour Pagination et Recherche ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Nombre d'éléments par page
  const [totalClients, setTotalClients] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  // --- NOUVEAUX ÉTATS pour le Tri ---
  const [sortColumn, setSortColumn] = useState('created_at'); // Colonne de tri par défaut
  const [sortDirection, setSortDirection] = useState('desc'); // Direction par défaut (descendant)
  // ---------------------------------

  // Fonction pour récupérer les clients (MODIFIÉE pour le tri)
  // Utiliser useCallback pour éviter recréation inutile
  const fetchClients = useCallback(async (page = currentPage, search = searchTerm, orderBy = sortColumn, ascending = sortDirection === 'asc') => {
    setIsLoading(true);
    setError(null);
    
    const offset = (page - 1) * itemsPerPage;
    
    // Préparer les filtres, y compris le tri
    const filters = {
      limit: itemsPerPage,
      offset: offset,
      orderBy: orderBy, // Ajouter le paramètre de tri
      ascending: ascending // Ajouter la direction
    };
    if (search && search.trim() !== '') {
      // Simple approche: chercher dans le nom ET le téléphone
      // On pourrait avoir un select pour choisir le champ ou une logique plus fine
      filters.name = search.trim(); 
      filters.phone = search.trim(); 
      // NOTE: Le backend actuel applique un OR implicite s'il reçoit plusieurs filtres ILIKE
      // Si on voulait un filtre spécifique, il faudrait adapter le backend ou passer un seul filtre.
    }

    try {
      // Appeler l'API avec les filtres
      const response = await clientsApi.getAll(filters);
      if (response && response.data) {
        // Mettre à jour la liste ET le total
        setClients(response.data.customers || []); 
        setTotalClients(response.data.totalCount || 0);
      } else {
        console.warn('Aucune donnée client reçue de l\'API.');
        setClients([]);
        setTotalClients(0);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des clients:", err);
      setError("Une erreur est survenue lors du chargement des clients.");
      setClients([]);
      setTotalClients(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, sortColumn, sortDirection]); // Dépendances de useCallback

  // useEffect pour charger les données initialement et lors des changements
//   useEffect(() => {
//     fetchClients(currentPage, searchTerm);
//   }, [fetchClients, currentPage, searchTerm]); // Exécuter quand fetchClients, currentPage ou searchTerm change

  // --- GESTIONNAIRES D'ÉVÉNEMENTS Pagination/Recherche ---
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= Math.ceil(totalClients / itemsPerPage)) {
      setCurrentPage(newPage);
    }
  };

  // Debounce la fonction de recherche pour éviter trop d'appels API
  const debouncedSearch = useCallback(
    debounce((value) => {
      // Ne fait QUE mettre à jour la page si le terme de recherche change
      // L'appel API sera déclenché par le useEffect qui écoute searchTerm et currentPage
      if (value !== searchTerm) { // Important: vérifier si le terme a réellement changé
          setCurrentPage(1); // Revenir à la première page lors d'une nouvelle recherche
      }
      // fetchClients(1, value); // Ne plus appeler fetchClients ici
    }, 500), 
    [searchTerm] // Dépendance sur searchTerm pour comparer avec la nouvelle valeur
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value); // Mettre à jour l'état du terme de recherche
    // Appeler la fonction debounced qui mettra à jour la page (si nécessaire) après délai
    debouncedSearch(value); 
  };

  // Le useEffect principal gère maintenant tous les appels API
  useEffect(() => {
      // Appelle fetchClients quand currentPage ou searchTerm change
      fetchClients(currentPage, searchTerm);
  // Les dépendances doivent être stables. fetchClients est stable grâce à useCallback.
  // Si fetchClients n'était pas dans useCallback, il faudrait lister ses dépendances internes ici.
  }, [currentPage, searchTerm, fetchClients]); 

  // --- NOUVELLE FONCTION pour gérer le Tri ---
  const handleSort = (column) => {
    // Si on clique sur la même colonne, inverser la direction
    if (column === sortColumn) {
      setSortDirection(prevDirection => prevDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Si on clique sur une nouvelle colonne, la définir et trier par défaut en ascendant
      setSortColumn(column);
      setSortDirection('asc');
    }
    // Revenir à la première page quand le tri change
    setCurrentPage(1); 
    // Le useEffect principal s'occupera de rafraîchir les données
  };
  // -----------------------------------------

  // Fonction pour gérer le clic sur le bouton d'import
  const handleImportShopify = async () => {
    setIsImporting(true); // Commencer l'importation
    setImportError(null); // Réinitialiser les erreurs d'import
    setImportSuccessMessage(null); // Réinitialiser le message de succès

    try {
      // Appeler l'API d'importation (sans passer les credentials)
      const response = await integrationsApi.importShopifyCustomers();
      
      if (response && response.data && response.data.success) {
        setImportSuccessMessage(response.data.message || 'Importation Shopify terminée avec succès.');
        // Rafraîchir la liste des clients après succès
        await fetchClients(); 
      } else {
        // Gérer les erreurs retournées par l'API même si la requête a réussi (code 2xx)
        const errorMessage = response?.data?.message || 'Une erreur inconnue est survenue lors de l\'importation.';
        console.error('Erreur API lors de l\'importation Shopify:', response?.data);
        setImportError(errorMessage);
      }
    } catch (err) {
      // Gérer les erreurs réseau ou les erreurs 5xx/4xx non gérées par l'API elle-même
      console.error("Erreur lors de l'appel d'importation Shopify:", err);
      const errorMessage = err.response?.data?.message || err.message || "Une erreur réseau ou serveur est survenue.";
      // Vérifier si c'est l'erreur spécifique des credentials manquants
      if (errorMessage.includes('Could not retrieve Shopify credentials')) {
          setImportError('Impossible de lancer l\'importation. Veuillez vérifier vos paramètres de connexion Shopify.');
      } else {
          setImportError(`Erreur lors de l'importation: ${errorMessage}`);
      }
    } finally {
      setIsImporting(false); // Terminer l'importation (succès ou échec)
    }
  };

  // Fonction pour calculer les points d'un client spécifique
  const handleCalculatePoints = async (customerId) => {
    // Indiquer que le calcul est en cours pour ce client
    setCalculatingPoints(prev => ({ ...prev, [customerId]: true }));
    // Réinitialiser les messages généraux d'import au cas où
    setImportError(null);
    setImportSuccessMessage(null);

    try {
      const response = await clientsApi.calculateShopifyPoints(customerId);
      console.log('response', response);
      if (response && response.data && response.data.success) {
        // Mettre à jour l'état local du client avec les nouveaux points
        setClients(prevClients => prevClients.map(client => 
          client.id === customerId 
            // Mettre à jour l'objet customer_points ou le créer s'il n'existait pas
            ? { ...client, customer_points: [{ total_points: response.data.points } ]} 
            : client
        ));
        // Optionnel: afficher un petit message de succès temporaire ?
        console.log(`Points calculés pour ${customerId}: ${response.data.points}`);
      } else {
        // Gérer les erreurs retournées par l'API (4xx)
        const errorMsg = response?.data?.error || "Erreur lors du calcul des points.";
        console.error(`Erreur calcul points pour ${customerId}:`, errorMsg);
        // Afficher l'erreur à l'utilisateur (peut-être à côté du bouton?)
        // Pour l'instant, on loggue juste.
        alert(`Erreur pour client ${customerId}: ${errorMsg}`); // Simple alerte pour l'instant
      }
    } catch (err) {
      // Gérer les erreurs réseau/serveur (5xx)
      console.error(`Erreur réseau calcul points pour ${customerId}:`, err);
      alert(`Erreur réseau/serveur lors du calcul des points pour client ${customerId}.`);
    } finally {
      // Indiquer que le calcul est terminé pour ce client
      setCalculatingPoints(prev => ({ ...prev, [customerId]: false }));
    }
  };

  // --- NOUVELLE FONCTION pour Calcul Batch ---
  const handleCalculatePointsBatch = async () => {
    setIsBatchCalculating(true);
    setBatchMessage('Lancement du calcul par lot...'); // Message initial
    setImportError(null); // Nettoyer autres messages
    setImportSuccessMessage(null);

    // Préparer les filtres basés sur le terme de recherche actuel
    const filters = {};
    if (searchTerm && searchTerm.trim() !== '') {
      filters.name = searchTerm.trim();
      filters.phone = searchTerm.trim();
    }

    try {
      const response = await clientsApi.calculateShopifyPointsBatch(filters);
      if (response && response.data) {
        // Afficher le message résumé du backend
        setBatchMessage(response.data.message || 'Traitement par lot terminé.');
        // Rafraîchir la liste actuelle APRÈS la fin du batch pour voir les mises à jour
        // Note: Le rafraîchissement ne montrera que la page actuelle, 
        // mais les points ont été calculés pour tous les clients filtrés.
        await fetchClients(currentPage, searchTerm);
      } else {
        setBatchMessage('Erreur lors du traitement par lot. Réponse invalide du serveur.');
      }
    } catch (error) { // Erreur lors de l'appel API lui-même
      console.error('Erreur appel API batch points calc:', error);
      setBatchMessage('Erreur réseau ou serveur lors du lancement du calcul par lot.');
    } finally {
      setIsBatchCalculating(false);
      // On pourrait effacer le message après quelques secondes
      // setTimeout(() => setBatchMessage(null), 10000); 
    }
  };
  // -----------------------------------------

  // --- NOUVELLE FONCTION pour Calcul par Page ---
  const handleCalculatePointsForPage = async () => {
    // Vérifier s'il y a des clients sur la page actuelle
    if (!clients || clients.length === 0) {
      setPageCalcMessage("Aucun client à traiter sur cette page.");
      return;
    }

    setIsPageCalculating(true); // Indiquer le début du calcul
    setPageCalcMessage('Calcul des points pour les clients de la page en cours...'); // Message initial
    // Réinitialiser les autres messages pour éviter la confusion
    setImportError(null);
    setImportSuccessMessage(null);
    setBatchMessage(null);

    // Récupérer les IDs des clients actuellement affichés
    const customerIdsOnPage = clients.map(client => client.id);

    try {
      // Appeler la NOUVELLE fonction API (à créer dans api.js et sur le serveur)
      // Elle devrait accepter un tableau d'IDs
      const response = await clientsApi.calculateShopifyPointsForPage(customerIdsOnPage);

      if (response && response.data && response.data.success) {
        // Afficher le message de succès du backend
        setPageCalcMessage(response.data.message || `Points calculés avec succès pour ${response.data.processedCount || customerIdsOnPage.length} client(s) de la page.`);
        // Rafraîchir les données de la page actuelle pour afficher les points mis à jour
        // Note : Si l'API backend renvoie les clients mis à jour, on pourrait optimiser
        // en mettant à jour l'état `clients` directement ici, comme dans `handleCalculatePoints`.
        await fetchClients(currentPage, searchTerm); 
      } else {
        // Gérer les erreurs retournées par l'API même si la requête aboutit (ex: erreur partielle)
        const errorMsg = response?.data?.message || "Une erreur inconnue est survenue lors du calcul des points pour la page.";
        setPageCalcMessage(`Erreur: ${errorMsg}`);
        console.error('Erreur API lors du calcul par page:', response?.data);
      }
    } catch (error) {
      // Gérer les erreurs réseau ou serveur (5xx)
      console.error('Erreur appel API calcul points page:', error);
      const errorMsg = error.response?.data?.message || error.message || "Erreur réseau ou serveur.";
      setPageCalcMessage(`Erreur lors du calcul des points pour la page: ${errorMsg}`);
    } finally {
      setIsPageCalculating(false); // Indiquer la fin du calcul
      // Optionnel : effacer le message après quelques secondes
      // setTimeout(() => setPageCalcMessage(null), 10000);
    }
  };
  // -------------------------------------------

  // Calcul du nombre total de pages
  const totalPages = Math.ceil(totalClients / itemsPerPage);

  // Gestionnaire pour le bouton Détails
  const handleShowDetails = (clientId) => {
    setSelectedClientId(clientId);
    setIsDetailsModalVisible(true);
  };

  // Gestionnaire pour fermer le modal de détails
  const handleCloseDetailsModal = () => {
    setIsDetailsModalVisible(false);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-700">Gestion des Clients</h1>
        <div>
          <button 
            onClick={handleImportShopify}
            className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-300 mr-2 ${
              isImporting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isImporting}
          >
            {isImporting ? 'Importation...' : 'Importer Shopify'}
          </button>
          <button 
            onClick={handleCalculatePointsBatch}
            className={`px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition duration-300 mr-2 ${
              isBatchCalculating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isBatchCalculating || isPageCalculating} // Désactiver aussi si le calcul par page est en cours
          >
            {isBatchCalculating ? 'Calcul en cours...' : 'Calculer Points (Tous Filtres)'}
          </button>
          <button 
            onClick={handleCalculatePointsForPage}
            className={`px-4 py-2 bg-yellow-500 text-black rounded hover:bg-yellow-600 transition duration-300 mr-2 ${
              isPageCalculating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isPageCalculating || isBatchCalculating} // Désactiver si l'un ou l'autre calcul est en cours
          >
            {isPageCalculating ? 'Calcul Page...' : 'Calculer Points (Page)'}
          </button>
        </div>
      </div>

      {/* Affichage des messages d'importation */}
      {importError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-400 rounded">
          {importError}
        </div>
      )}
      {importSuccessMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-400 rounded">
          {importSuccessMessage}
        </div>
      )}

      {/* Affichage message batch */} 
      {batchMessage && (
        <div className={`mb-4 p-3 rounded ${isBatchCalculating ? 'bg-yellow-100 text-yellow-700 border border-yellow-400' : 'bg-blue-100 text-blue-700 border border-blue-400'}`}>
          {batchMessage}
        </div>
      )}

      {/* NOUVEAU Message pour le calcul par page */}
      {pageCalcMessage && <p className={`mb-4 ${pageCalcMessage.startsWith('Erreur') ? 'text-red-600' : 'text-orange-600'}`}>{pageCalcMessage}</p>}

      {/* --- Barre de Recherche --- */}
      <div className="mb-4">
        <input 
          type="text"
          placeholder="Rechercher par nom ou téléphone..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />
      </div>
      {/* ------------------------ */}

      {/* Zone pour afficher la liste des clients */}
      <div className="bg-white shadow rounded-lg p-4">
        <h2 className="text-xl font-semibold text-gray-600 mb-4">Liste des Clients</h2>
        
        {/* Affichage conditionnel basé sur l'état de chargement et d'erreur */}
        {isLoading ? (
          <p className="text-center text-gray-500">Chargement des clients...</p>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : clients.length > 0 ? (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* En-tête cliquable pour le tri par Nom */}
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')} // Appel handleSort avec 'name'
                  >
                    Nom 
                    {/* Indicateur de tri */}
                    {sortColumn === 'name' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Téléphone
                    {/* onClick={() => handleSort('phone')} - À activer si le tri par téléphone est souhaité et implémenté */} 
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                    {/* onClick={() => handleSort('source')} - À activer si tri souhaité/implémenté */}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Blacklisté
                    {/* onClick={() => handleSort('is_blacklisted')} - À activer si tri souhaité/implémenté */}
                  </th>
                  {/* En-tête cliquable pour le tri par Points */}
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('points')} // Appel handleSort avec 'points'
                  >
                    Points
                    {/* Indicateur de tri */}
                    {sortColumn === 'points' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clients.map((client) => {
                  // Vérifier si le calcul est en cours pour ce client
                  const isCalculating = calculatingPoints[client.id]; 
                  // Extraire les points (avec 0 par défaut si customer_points n'existe pas ou est null)
                  const points = client.customer_points[0]?.total_points ?? 0;

                  return (
                    <tr key={client.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{client.name || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.phone || 'N/A'}</td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.email || 'N/A'}</td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.source || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {client.is_blacklisted ? 
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Oui</span> : 
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Non</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{points}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          className="text-indigo-600 hover:text-indigo-900 mr-2"
                          onClick={() => handleShowDetails(client.id)}
                        >
                          Détails
                        </button>
                        {/* Ajouter le bouton de calcul seulement pour les clients Shopify */}
                        {(client.source === 'shopify_migration' || client.source === 'shopify') && (
                          <button 
                            onClick={() => handleCalculatePoints(client.id)}
                            className={`text-blue-600 hover:text-blue-900 ${
                              isCalculating ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            disabled={isCalculating}
                          >
                            {isCalculating ? 'Calcul...' : 'Calculer Points (Shopify)'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* --- Contrôles de Pagination --- */}
            {totalClients > itemsPerPage && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-700">
                  Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, totalClients)} sur {totalClients} clients
                </span>
                <div>
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Précédent
                  </button>
                  <span className="mx-2 text-sm">Page {currentPage} sur {totalPages}</span>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
            {/* ---------------------------- */}
          </>
        ) : (
          <p className="text-center text-gray-500">Aucun client trouvé {searchTerm ? 'pour "'+searchTerm+'"' : '.'}</p>
        )}
        
      </div>

      {/* Modal de détails client */}
      <ClientDetails 
        clientId={selectedClientId} 
        visible={isDetailsModalVisible} 
        onClose={handleCloseDetailsModal}
      />
    </div>
  );
};

export default Clients;
